import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
} from './siteConfig';

function truncateDescription(text, maxLength = 160) {
  const normalized = (text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return DEFAULT_DESCRIPTION;
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1)}…`;
}

function PageMeta({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  jsonLd = null,
}) {
  const pageTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const pageDescription = truncateDescription(description);
  const canonicalUrl = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export function buildOrganizationSchema() {
  return {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description: DEFAULT_DESCRIPTION,
  };
}

export function buildWebSiteSchema() {
  // SearchAction omitted until browse/search accepts a stable URL query param.
  return {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
  };
}

export function buildHomePageSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [buildOrganizationSchema(), buildWebSiteSchema()],
  };
}

export function buildFaqPageSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.join(' '),
      },
    })),
  };
}

export function buildGearProductSchema(gear, reviews = []) {
  if (!gear) return null;

  const ratings = reviews.filter((review) => review.rating);
  const aggregateRating = ratings.length
    ? {
        '@type': 'AggregateRating',
        ratingValue: (
          ratings.reduce((sum, review) => sum + (review.rating || 0), 0) / ratings.length
        ).toFixed(1),
        reviewCount: ratings.length,
      }
    : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: gear.name,
    description: gear.description || `${gear.name} available for rent on ${SITE_NAME}.`,
    image: gear.mainPicture ? [gear.mainPicture] : undefined,
    category: gear.category || 'Outdoor Gear',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: Number(gear.pricePerDay) || undefined,
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/gear/${gear.id}`,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: Number(gear.pricePerDay) || undefined,
        priceCurrency: 'USD',
        unitText: 'day',
      },
    },
    ...(aggregateRating ? { aggregateRating } : {}),
  };
}

export default PageMeta;